// =====================================================================
// Prelevements.jsx - Pagina principala unificata Prelevements (Day 4)
// =====================================================================
// Layout Option C: 1 link in sidebar (/prelevements), 3 vue-uri interne.
//
// Tabs:
//   1. Prelevements      - PrelevementsList embedded
//   2. Facturation       - pivot Projet x mois + bulk invoice/uninvoice
//   3. Types             - SupplyTypesTab (CRUD complet inline)
//
// Permission gates:
//   - tab 1: supply_draws.read.{all,own}
//   - tab 2: supply_draws.invoice.all
//   - tab 3: supply_draws.manage_catalog.all
// =====================================================================

import { useState, useEffect, useCallback } from 'react';
import {
  supplyDraws as drawsApi,
  supplyTypes as typesApi,
} from '../../services/api.js';
import { useAuth } from '../../hooks/useAuth.jsx';
import PrelevementsList from './PrelevementsList.jsx';
import Breadcrumbs from '../../components/Breadcrumbs.jsx';

const CATEGORY_LABELS_FR = {
  filmage:   'Filmage',
  carton:    'Carton',
  etiquette: 'Étiquette',
  adhesif:   'Adhésif',
  papier:    'Papier',
  pochette:  'Pochette',
};

const CATEGORY_COLORS = {
  filmage:   { bg: '#dbeafe', fg: '#1e40af', border: '#93c5fd' },
  carton:    { bg: '#fef3c7', fg: '#78350f', border: '#fbbf24' },
  etiquette: { bg: '#fce7f3', fg: '#831843', border: '#f472b6' },
  adhesif:   { bg: '#fed7aa', fg: '#7c2d12', border: '#fb923c' },
  papier:    { bg: '#e0e7ff', fg: '#3730a3', border: '#818cf8' },
  pochette:  { bg: '#d1fae5', fg: '#065f46', border: '#34d399' },
};

const tabBtn = (active) => ({
  background: 'transparent', border: 0,
  padding: '0.75rem 1.25rem', fontSize: '0.875rem',
  fontWeight: active ? 600 : 400,
  color: active ? '#2563eb' : '#6b7280',
  borderBottom: active ? '2px solid #2563eb' : '2px solid transparent',
  cursor: 'pointer', marginBottom: -1,
});

const inputBase = {
  padding: '0.4rem 0.5rem', border: '1px solid #d1d5db',
  borderRadius: 4, fontSize: '0.8125rem', background: '#fff',
};

const btnSuccess = {
  background: '#059669', color: '#fff', border: 0,
  padding: '0.5rem 1rem', borderRadius: 4,
  fontWeight: 500, fontSize: '0.875rem', cursor: 'pointer',
};

const btnSecondary = {
  background: 'white', color: '#1e293b', border: '1px solid #d1d5db',
  padding: '0.5rem 1rem', borderRadius: 4,
  fontWeight: 500, fontSize: '0.875rem', cursor: 'pointer',
};

const thStyle = {
  padding: '0.625rem 0.75rem', fontSize: '0.6875rem', fontWeight: 600,
  color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em',
  textAlign: 'left', borderBottom: '1px solid #e5e7eb',
};

const tdStyle = {
  padding: '0.625rem 0.75rem', verticalAlign: 'middle',
  color: '#374151', fontSize: '0.875rem',
};

function formatDateFR(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch { return ''; }
}

// =====================================================================
// MAIN
// =====================================================================
export default function Prelevements() {
  const { can } = useAuth();

  const canRead          = can(['supply_draws.read.all', 'supply_draws.read.own']);
  const canInvoice       = can('supply_draws.invoice.all');
  const canManageCatalog = can('supply_draws.manage_catalog.all');

  const defaultTab = canRead ? 'prelevements' : (canInvoice ? 'facturation' : (canManageCatalog ? 'types' : 'prelevements'));
  const [activeTab, setActiveTab] = useState(defaultTab);

  const TAB_LABELS = {
    'prelevements': 'Prélev. fourn.',
    'facturation':  'Facturation',
    'types':        'Types (catalogue)',
  };

  if (!canRead && !canInvoice && !canManageCatalog) {
    return (
      <div style={{ padding: 40 }}>
        <h1>Prélev. fourn.</h1>
        <p style={{ color: '#6b7280', marginTop: 16 }}>Permission refusée.</p>
      </div>
    );
  }

  return (
    <div style={{ padding: '1.5rem 2rem' }}>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
        <h1 style={{ margin: 0, fontSize: '1.5rem', fontWeight: 600 }}>Prélev. fourn.</h1>
        <span style={{ fontSize: '0.75rem', color: '#9ca3af', fontFamily: 'monospace' }}>M-001 — fournitures &amp; re-facturation</span>
      </div>
      <p className="hide-mobile" style={{ color: '#6b7280', fontSize: '0.875rem', margin: '0 0 1.25rem' }}>
        Prélev. fourn. de matériaux d'emballage et re-facturation aux projets.
      </p>

      <Breadcrumbs items={[
        { label: 'Prélev. fourn.', onClick: () => setActiveTab(defaultTab) },
        { label: TAB_LABELS[activeTab] || activeTab },
      ]} />

      <div className="mobile-tabs-scroll" style={{ borderBottom: '1px solid #e5e7eb', marginBottom: '1.5rem' }}>
        {canRead && (
          <button style={tabBtn(activeTab === 'prelevements')} onClick={() => setActiveTab('prelevements')}>
            📦 Prélev. fourn.
          </button>
        )}
        {canInvoice && (
          <button style={tabBtn(activeTab === 'facturation')} onClick={() => setActiveTab('facturation')}>
            💰 Facturation
          </button>
        )}
        {canManageCatalog && (
          <button style={tabBtn(activeTab === 'types')} onClick={() => setActiveTab('types')}>
            🏷️ Types (catalogue)
          </button>
        )}
      </div>

      {activeTab === 'prelevements' && canRead          && <PrelevementsList embedded />}
      {activeTab === 'facturation'  && canInvoice       && <FacturationTab />}
      {activeTab === 'types'        && canManageCatalog && <SupplyTypesTab />}
    </div>
  );
}

// =====================================================================
// TAB FACTURATION (pivot Projet x Mois)
// =====================================================================
function FacturationTab() {
  const [draws, setDraws]             = useState([]);
  const [loading, setLoading]         = useState(true);
  const [error, setError]             = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkBusy, setBulkBusy]       = useState(false);

  const [year, setYear]   = useState(() => new Date().getFullYear());
  const [month, setMonth] = useState(() => new Date().getMonth() + 1);

  const fetchDraws = useCallback(async () => {
    setLoading(true);
    setError(null);
    setSelectedIds(new Set());
    try {
      const dateFrom = `${year}-${String(month).padStart(2, '0')}-01`;
      const lastDay = new Date(year, month, 0).getDate();
      const dateTo  = `${year}-${String(month).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;

      const res = await drawsApi.list({ dateFrom, dateTo, pageSize: 500 });
      setDraws(res.items || []);
    } catch (err) {
      console.error('[Prelevements/FacturationTab] fetch:', err);
      setError(err.response?.data?.message || 'Erreur lors du chargement');
      setDraws([]);
    } finally {
      setLoading(false);
    }
  }, [year, month]);

  useEffect(() => { fetchDraws(); }, [fetchDraws]);

  // Pivot Project x mois
  const pivot = (() => {
    const byProject = new Map();
    for (const d of draws) {
      const pkey = d.projectId || '(none)';
      const pname = d.project?.name || '(sans projet)';
      if (!byProject.has(pkey)) {
        byProject.set(pkey, {
          key: pkey, name: pname, draws: [],
          allInvoiced: true, totalQty: 0, byCategory: {},
        });
      }
      const pr = byProject.get(pkey);
      pr.draws.push(d);
      pr.totalQty += d.quantity || 0;
      const cat = d.supplyType?.category || 'other';
      pr.byCategory[cat] = (pr.byCategory[cat] || 0) + (d.quantity || 0);
      if (!d.invoiced) pr.allInvoiced = false;
    }
    for (const pr of byProject.values()) {
      pr.draws.sort((a, b) => (a.date || '').localeCompare(b.date || ''));
    }
    return Array.from(byProject.values()).sort((a, b) => a.name.localeCompare(b.name));
  })();

  function toggleProject(pr) {
    const next = new Set(selectedIds);
    const eligible = pr.draws.filter(d => !d.invoiced).map(d => d.id);
    const allSelected = eligible.length > 0 && eligible.every(id => next.has(id));
    if (allSelected) eligible.forEach(id => next.delete(id));
    else eligible.forEach(id => next.add(id));
    setSelectedIds(next);
  }

  function toggleDraw(drawId, invoiced) {
    if (invoiced) return;
    const next = new Set(selectedIds);
    if (next.has(drawId)) next.delete(drawId);
    else next.add(drawId);
    setSelectedIds(next);
  }

  function toggleSelectAll() {
    const eligible = draws.filter(d => !d.invoiced).map(d => d.id);
    if (eligible.every(id => selectedIds.has(id))) setSelectedIds(new Set());
    else setSelectedIds(new Set(eligible));
  }

  async function handleBulkInvoice() {
    if (selectedIds.size === 0) return;
    if (!confirm(`Marquer ${selectedIds.size} prélèvement(s) comme facturé(s) ?`)) return;
    setBulkBusy(true);
    try {
      const res = await drawsApi.bulkInvoice({ ids: Array.from(selectedIds) });
      alert(`Facturés: ${res.invoiced || 0}\nDéjà facturés (skip): ${res.alreadyInvoiced || 0}\nIntrouvables: ${res.notFound || 0}`);
      await fetchDraws();
    } catch (err) {
      alert('Erreur: ' + (err.response?.data?.message || err.message));
    } finally { setBulkBusy(false); }
  }

  async function handleBulkUninvoice() {
    if (selectedIds.size === 0) return;
    if (!confirm(`Annuler la facturation pour ${selectedIds.size} prélèvement(s) ?`)) return;
    setBulkBusy(true);
    try {
      const res = await drawsApi.bulkUninvoice({ ids: Array.from(selectedIds) });
      alert(`Réversés: ${res.uninvoiced || 0}\nDéjà non facturés (skip): ${res.alreadyUninvoiced || 0}\nIntrouvables: ${res.notFound || 0}`);
      await fetchDraws();
    } catch (err) {
      alert('Erreur: ' + (err.response?.data?.message || err.message));
    } finally { setBulkBusy(false); }
  }

  const years = [year - 1, year, year + 1];
  const months = [
    { v: 1, l: 'Janvier' }, { v: 2, l: 'Février' }, { v: 3, l: 'Mars' }, { v: 4, l: 'Avril' },
    { v: 5, l: 'Mai' }, { v: 6, l: 'Juin' }, { v: 7, l: 'Juillet' }, { v: 8, l: 'Août' },
    { v: 9, l: 'Septembre' }, { v: 10, l: 'Octobre' }, { v: 11, l: 'Novembre' }, { v: 12, l: 'Décembre' },
  ];

  const grandTotal = pivot.reduce((acc, p) => acc + p.totalQty, 0);

  return (
    <>
      <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', alignItems: 'center', marginBottom: '1rem' }}>
        <span style={{ fontSize: '0.875rem', color: '#374151', fontWeight: 500 }}>Période:</span>
        <select value={month} onChange={e => setMonth(parseInt(e.target.value, 10))} style={inputBase}>
          {months.map(m => <option key={m.v} value={m.v}>{m.l}</option>)}
        </select>
        <select value={year} onChange={e => setYear(parseInt(e.target.value, 10))} style={inputBase}>
          {years.map(y => <option key={y} value={y}>{y}</option>)}
        </select>

        <div style={{ marginLeft: 'auto', display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
          <span style={{ fontSize: '0.8125rem', color: '#6b7280' }}>{selectedIds.size} sélectionné{selectedIds.size !== 1 ? 's' : ''}</span>
          <button style={{ ...btnSuccess, opacity: (selectedIds.size === 0 || bulkBusy) ? 0.5 : 1 }} disabled={selectedIds.size === 0 || bulkBusy} onClick={handleBulkInvoice}>
            ✓ Marquer facturé
          </button>
          <button style={{ ...btnSecondary, opacity: (selectedIds.size === 0 || bulkBusy) ? 0.5 : 1 }} disabled={selectedIds.size === 0 || bulkBusy} onClick={handleBulkUninvoice}>
            Annuler facturation
          </button>
        </div>
      </div>

      {!loading && draws.length > 0 && (
        <div style={{ display: 'flex', gap: '1.5rem', flexWrap: 'wrap', padding: '0.625rem 0.875rem', background: '#f9fafb', borderRadius: 4, marginBottom: '1rem', fontSize: '0.8125rem', color: '#374151' }}>
          <span>📊 <strong>{draws.length}</strong> prélèvements</span>
          <span>Σ qté: <strong>{grandTotal.toLocaleString('fr-BE')}</strong></span>
          <span><strong>{pivot.length}</strong> projet(s)</span>
        </div>
      )}

      {error && <div style={{ background: '#fee2e2', color: '#991b1b', padding: '0.75rem 1rem', borderRadius: 4, marginBottom: '1rem' }}>{error}</div>}
      {loading && <div style={{ padding: 40, textAlign: 'center', color: '#6b7280' }}>Chargement...</div>}

      {!loading && draws.length === 0 && (
        <div style={{ padding: 40, textAlign: 'center', color: '#9ca3af', background: '#f9fafb', borderRadius: 6 }}>
          Aucun prélèvement pour cette période.
        </div>
      )}

      {!loading && draws.length > 0 && (
        <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 6, overflow: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8125rem' }}>
            <thead style={{ background: '#f9fafb' }}>
              <tr>
                <th style={{ ...thStyle, width: 36 }}>
                  <input
                    type="checkbox"
                    checked={draws.filter(d => !d.invoiced).every(d => selectedIds.has(d.id)) && draws.some(d => !d.invoiced)}
                    onChange={toggleSelectAll}
                  />
                </th>
                <th style={thStyle}>Projet / Prélèvement</th>
                <th style={thStyle}>Date</th>
                <th style={thStyle}>Type</th>
                <th style={thStyle}>Catégorie</th>
                <th style={{ ...thStyle, textAlign: 'right' }}>Quantité</th>
                <th style={{ ...thStyle, textAlign: 'center' }}>Statut</th>
              </tr>
            </thead>
            <tbody>
              {pivot.map(pr => (
                <ProjectRows key={pr.key} pr={pr} selectedIds={selectedIds} onToggleProject={() => toggleProject(pr)} onToggleDraw={toggleDraw} />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

function ProjectRows({ pr, selectedIds, onToggleProject, onToggleDraw }) {
  const eligibleIds = pr.draws.filter(d => !d.invoiced).map(d => d.id);
  const totalSel = eligibleIds.filter(id => selectedIds.has(id)).length;
  const allSel  = eligibleIds.length > 0 && totalSel === eligibleIds.length;
  const someSel = totalSel > 0 && totalSel < eligibleIds.length;

  return (
    <>
      <tr style={{ borderTop: '2px solid #e5e7eb', background: '#fafafa' }}>
        <td style={{ ...tdStyle, padding: '0.5rem 0.75rem' }}>
          <input
            type="checkbox"
            checked={allSel}
            ref={el => { if (el) el.indeterminate = someSel; }}
            onChange={onToggleProject}
            disabled={eligibleIds.length === 0}
          />
        </td>
        <td style={{ ...tdStyle, padding: '0.5rem 0.75rem', fontWeight: 600, color: '#111827' }} colSpan={4}>
          {pr.name}
          <span style={{ marginLeft: 8, fontSize: '0.6875rem', color: '#6b7280', fontWeight: 400 }}>
            · {pr.draws.length} prélèv. {Object.entries(pr.byCategory).map(([c, q]) => `${CATEGORY_LABELS_FR[c] || c}: ${q}`).join(' · ')}
          </span>
        </td>
        <td style={{ ...tdStyle, padding: '0.5rem 0.75rem', textAlign: 'right', fontWeight: 600 }}>{pr.totalQty.toLocaleString('fr-BE')}</td>
        <td style={{ ...tdStyle, padding: '0.5rem 0.75rem', textAlign: 'center' }}>
          <span style={{
            background: pr.allInvoiced ? '#d1fae5' : '#fef3c7',
            color: pr.allInvoiced ? '#065f46' : '#92400e',
            padding: '0.125rem 0.5rem', borderRadius: 12, fontSize: '0.6875rem', fontWeight: 500,
          }}>
            {pr.allInvoiced ? '✓ Tous facturés' : 'Non facturé'}
          </span>
        </td>
      </tr>
      {pr.draws.map(d => {
        const cat = d.supplyType?.category;
        const c = CATEGORY_COLORS[cat] || { bg: '#f1f5f9', fg: '#475569', border: '#cbd5e1' };
        return (
          <tr key={d.id} style={{ borderTop: '1px solid #f3f4f6' }}>
            <td style={tdStyle}>
              <input
                type="checkbox"
                checked={selectedIds.has(d.id)}
                onChange={() => onToggleDraw(d.id, d.invoiced)}
                disabled={d.invoiced}
              />
            </td>
            <td style={{ ...tdStyle, padding: '0.4rem 0.75rem 0.4rem 2rem', fontSize: '0.75rem', fontFamily: 'monospace', color: '#6b7280' }}>{d.ref}</td>
            <td style={tdStyle}>{formatDateFR(d.date)}</td>
            <td style={tdStyle}>
              <span style={{ fontWeight: 500 }}>{d.supplyType?.code}</span>
              <span style={{ color: '#6b7280', marginLeft: 4, fontSize: '0.75rem' }}>{d.supplyType?.label}</span>
            </td>
            <td style={tdStyle}>
              {cat && (
                <span style={{
                  display: 'inline-block', padding: '2px 8px', fontSize: 11, fontWeight: 500,
                  background: c.bg, color: c.fg, border: `1px solid ${c.border}`, borderRadius: 3,
                }}>
                  {CATEGORY_LABELS_FR[cat] || cat}
                </span>
              )}
            </td>
            <td style={{ ...tdStyle, textAlign: 'right' }}>{d.quantity}</td>
            <td style={{ ...tdStyle, textAlign: 'center' }}>
              <span style={{
                background: d.invoiced ? '#d1fae5' : '#fef3c7',
                color: d.invoiced ? '#065f46' : '#92400e',
                padding: '0.125rem 0.5rem', borderRadius: 12, fontSize: '0.6875rem', fontWeight: 500,
              }}>
                {d.invoiced ? '✓' : '—'}
              </span>
            </td>
          </tr>
        );
      })}
    </>
  );
}

// =====================================================================
// TAB TYPES (CRUD complet)
// =====================================================================
function SupplyTypesTab() {
  const [items, setItems]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);
  const [showInactive, setShowInactive] = useState(false);
  const [search, setSearch]   = useState('');
  const [editing, setEditing] = useState(null);  // {id|null, code, label, unit, category, isActive}
  const [creating, setCreating] = useState(false);
  const [saving, setSaving]   = useState(false);

  const fetchTypes = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = {};
      if (!showInactive) params.isActive = true;
      const res = await typesApi.list(params);
      const arr = Array.isArray(res) ? res : (res.items || []);
      setItems(arr.sort((a, b) => (a.code || '').localeCompare(b.code || '')));
    } catch (err) {
      console.error('[SupplyTypesTab] fetch:', err);
      setError(err.response?.data?.message || 'Erreur lors du chargement');
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [showInactive]);

  useEffect(() => { fetchTypes(); }, [fetchTypes]);

  const filtered = items.filter(t => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (t.code || '').toLowerCase().includes(q) ||
           (t.label || '').toLowerCase().includes(q) ||
           (t.category || '').toLowerCase().includes(q);
  });

  function startCreate() {
    setCreating(true);
    setEditing({ id: null, code: '', label: '', unit: '', category: 'filmage', isActive: true });
  }
  function startEdit(item) {
    setCreating(false);
    setEditing({
      id: item.id,
      code: item.code || '',
      label: item.label || '',
      unit: item.unit || '',
      category: item.category || 'filmage',
      isActive: !!item.isActive,
    });
  }
  function cancelEdit() {
    setEditing(null);
    setCreating(false);
  }

  async function handleSave() {
    if (!editing) return;
    if (!editing.code.trim() || !editing.label.trim() || !editing.unit.trim()) {
      alert('Code, libellé et unité sont obligatoires.');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        code: editing.code.trim().toUpperCase(),
        label: editing.label.trim(),
        unit: editing.unit.trim(),
        category: editing.category,
        isActive: editing.isActive,
      };
      if (creating) {
        await typesApi.create(payload);
      } else {
        await typesApi.update(editing.id, payload);
      }
      cancelEdit();
      await fetchTypes();
    } catch (err) {
      alert('Erreur: ' + (err.response?.data?.message || err.message));
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(item) {
    if (!confirm(`Supprimer le type "${item.code}" ?\n\nSi des prélèvements existent, il sera désactivé.`)) return;
    setSaving(true);
    try {
      await typesApi.remove(item.id);
      await fetchTypes();
    } catch (err) {
      alert('Erreur: ' + (err.response?.data?.message || err.message));
    } finally {
      setSaving(false);
    }
  }

  return (
    <>
      <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', alignItems: 'center', marginBottom: '1rem' }}>
        <input
          type="text"
          placeholder="Rechercher (code, libellé, catégorie)..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={{ ...inputBase, flex: '1 1 240px' }}
        />
        <label style={{ fontSize: '0.8125rem', color: '#374151', display: 'flex', alignItems: 'center', gap: 4 }}>
          <input type="checkbox" checked={showInactive} onChange={e => setShowInactive(e.target.checked)} />
          Inclure inactifs
        </label>
        <span style={{ marginLeft: 'auto', fontSize: '0.8125rem', color: '#6b7280' }}>
          {filtered.length} / {items.length} type{items.length !== 1 ? 's' : ''}
        </span>
        <button onClick={startCreate} disabled={creating || editing != null} style={{
          padding: '8px 16px', background: '#2563eb', color: 'white',
          border: 'none', borderRadius: 4, fontSize: 13, fontWeight: 500,
          cursor: (creating || editing) ? 'not-allowed' : 'pointer',
          opacity: (creating || editing) ? 0.6 : 1,
        }}>
          + Nouveau type
        </button>
      </div>

      {error && <div style={{ background: '#fee2e2', color: '#991b1b', padding: '0.75rem 1rem', borderRadius: 4, marginBottom: '1rem' }}>{error}</div>}
      {loading && <div style={{ padding: 40, textAlign: 'center', color: '#6b7280' }}>Chargement...</div>}

      {!loading && (
        <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 6, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead style={{ background: '#f9fafb' }}>
              <tr>
                <th style={thStyle}>Code</th>
                <th style={thStyle}>Libellé</th>
                <th style={thStyle}>Unité</th>
                <th style={thStyle}>Catégorie</th>
                <th style={thStyle}>Statut</th>
                <th style={{ ...thStyle, textAlign: 'right' }}>Prélèv.</th>
                <th style={thStyle}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {creating && editing && (
                <EditRow editing={editing} setEditing={setEditing} onSave={handleSave} onCancel={cancelEdit} saving={saving} isNew />
              )}
              {filtered.length === 0 && !creating && (
                <tr><td colSpan={7} style={{ ...tdStyle, textAlign: 'center', color: '#9ca3af', padding: '2rem' }}>Aucun type à afficher.</td></tr>
              )}
              {filtered.map(item => (
                editing && editing.id === item.id ? (
                  <EditRow key={item.id} editing={editing} setEditing={setEditing} onSave={handleSave} onCancel={cancelEdit} saving={saving} />
                ) : (
                  <ViewRow key={item.id} item={item} onEdit={() => startEdit(item)} onDelete={() => handleDelete(item)} disabled={editing != null || saving} />
                )
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

function ViewRow({ item, onEdit, onDelete, disabled }) {
  const cat = item.category;
  const c = CATEGORY_COLORS[cat] || { bg: '#f1f5f9', fg: '#475569', border: '#cbd5e1' };
  return (
    <tr style={{ borderTop: '1px solid #f3f4f6' }}>
      <td style={{ ...tdStyle, fontFamily: 'monospace', fontWeight: 600 }}>{item.code}</td>
      <td style={tdStyle}>{item.label}</td>
      <td style={{ ...tdStyle, fontSize: '0.8125rem', color: '#6b7280' }}>{item.unit}</td>
      <td style={tdStyle}>
        {cat && (
          <span style={{
            display: 'inline-block', padding: '2px 8px', fontSize: 11, fontWeight: 500,
            background: c.bg, color: c.fg, border: `1px solid ${c.border}`, borderRadius: 3,
          }}>
            {CATEGORY_LABELS_FR[cat] || cat}
          </span>
        )}
      </td>
      <td style={tdStyle}>
        <span style={{
          background: item.isActive ? '#d1fae5' : '#f3f4f6',
          color: item.isActive ? '#065f46' : '#6b7280',
          padding: '0.125rem 0.5rem', borderRadius: 12, fontSize: '0.6875rem', fontWeight: 500,
        }}>
          {item.isActive ? 'Actif' : 'Inactif'}
        </span>
      </td>
      <td style={{ ...tdStyle, textAlign: 'right', fontSize: '0.8125rem', color: '#6b7280' }}>{item._count?.draws ?? '—'}</td>
      <td style={tdStyle}>
        <button onClick={onEdit} disabled={disabled} style={{
          padding: '4px 10px', background: 'white', color: '#1e293b',
          border: '1px solid #d1d5db', borderRadius: 4, fontSize: 12,
          cursor: disabled ? 'not-allowed' : 'pointer', marginRight: 4,
        }}>Modifier</button>
        <button onClick={onDelete} disabled={disabled} style={{
          padding: '4px 10px', background: 'white', color: '#991b1b',
          border: '1px solid #fca5a5', borderRadius: 4, fontSize: 12,
          cursor: disabled ? 'not-allowed' : 'pointer',
        }}>Supprimer</button>
      </td>
    </tr>
  );
}

function EditRow({ editing, setEditing, onSave, onCancel, saving, isNew }) {
  return (
    <tr style={{ borderTop: '1px solid #f3f4f6', background: '#f0f9ff' }}>
      <td style={tdStyle}>
        <input
          type="text"
          value={editing.code}
          onChange={e => setEditing({ ...editing, code: e.target.value.toUpperCase() })}
          disabled={!isNew}
          maxLength={20}
          placeholder="ex: FILM-STD"
          style={{ ...inputBase, fontFamily: 'monospace', width: 130, ...(!isNew ? { opacity: 0.5 } : {}) }}
        />
      </td>
      <td style={tdStyle}>
        <input
          type="text"
          value={editing.label}
          onChange={e => setEditing({ ...editing, label: e.target.value })}
          maxLength={100}
          placeholder="ex: Film étirable 23µ"
          style={{ ...inputBase, width: '100%' }}
        />
      </td>
      <td style={tdStyle}>
        <input
          type="text"
          value={editing.unit}
          onChange={e => setEditing({ ...editing, unit: e.target.value })}
          maxLength={10}
          placeholder="m, pcs, kg"
          style={{ ...inputBase, width: 80 }}
        />
      </td>
      <td style={tdStyle}>
        <select value={editing.category} onChange={e => setEditing({ ...editing, category: e.target.value })} style={inputBase}>
          {Object.entries(CATEGORY_LABELS_FR).map(([k, v]) => (
            <option key={k} value={k}>{v}</option>
          ))}
        </select>
      </td>
      <td style={tdStyle}>
        <label style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: '0.8125rem' }}>
          <input
            type="checkbox"
            checked={editing.isActive}
            onChange={e => setEditing({ ...editing, isActive: e.target.checked })}
          />
          Actif
        </label>
      </td>
      <td style={tdStyle}>—</td>
      <td style={tdStyle}>
        <button onClick={onSave} disabled={saving} style={{
          padding: '4px 10px', background: '#2563eb', color: 'white',
          border: 'none', borderRadius: 4, fontSize: 12, fontWeight: 500,
          cursor: saving ? 'not-allowed' : 'pointer', marginRight: 4,
          opacity: saving ? 0.6 : 1,
        }}>{saving ? '…' : (isNew ? 'Créer' : 'Enregistrer')}</button>
        <button onClick={onCancel} disabled={saving} style={{
          padding: '4px 10px', background: 'white', color: '#1e293b',
          border: '1px solid #d1d5db', borderRadius: 4, fontSize: 12,
          cursor: saving ? 'not-allowed' : 'pointer',
        }}>Annuler</button>
      </td>
    </tr>
  );
}
